import moment from "../moment/moment";

import * as constants from "../constants";
import { getActualShifts } from "./shifts";
import { sortByIdCallback } from "./utils";


export const CONFIRMED_RESERVATION = "CONFIRMED_RESERVATION";
export const PENDING_RESERVATION = "PENDING_RESERVATION";
export const CANCELLED_RESERVATION = "CANCELLED_RESERVATION";

export const filterReservationsByName = (value, reservations) => {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;
    const foundReservations = inputLength < 2 ? [] : reservations.filter(reservation => (
        reservation.last_name?.toLowerCase().slice(0, inputLength) === inputValue ||
        reservation.first_name?.toLowerCase().slice(0, inputLength) === inputValue));
    return getReservations(foundReservations);
};

export const getFilteredReservations = reservations => reservations.sort((a, b) => moment(a.start_time) - moment(b.start_time));

export const filterOldReservations = (reservations = []) => reservations.filter(reservation => !reservation.hidden && moment(reservation.start_time) >= moment().add(-2, "hour"));

export const getTimeline = (startTimeValue) => {
    const startTime = moment(startTimeValue).add(-3, 'hour').unix() * 1000;
    const endTime = moment(startTimeValue).add(3, 'hour').unix() * 1000;
    return { startTime: startTime, endTime: endTime };
};

export const getReservationFullName = reservation => {
    return `${reservation.last_name ?? ''} ${reservation.first_name ?? ''}`.trim() || constants.DEFAULT_CUSTOMER_FULLNAME;
}

export function getReservationGuests({ adults, children }) {
    return adults + children;
}

export const getReservation = reservation => {
    const guests = reservation.adults + reservation.children;
    const adults_name = reservation.adults > 1 ? constants.ADULTS_TEXT : constants.ADULT_TEXT;
    const children_name = reservation.children > 1 ? constants.CHILDREN_TEXT : constants.CHILD_TEXT;
    const children_part = reservation.children ? " " + constants.AND_TEXT + " " + reservation.children + " " + children_name : "";
    const guests_name = guests + " (" + reservation.adults + " " + adults_name + children_part + ")";
    const reservationFullName = getReservationFullName(reservation);
    return (
        {
            ...reservation,
            reservationStatus: reservation.cancelled ? CANCELLED_RESERVATION : reservation.confirmed ? CONFIRMED_RESERVATION : PENDING_RESERVATION,
            reservationLogo: reservation.cancelled ? "delete calendar" : reservation.confirmed ? "check" : "clock",
            reservationColor: reservation.cancelled ? "red" : reservation.confirmed ? "green" : "orange",
            reservationFullName,
            reservationGuestsDetails: guests_name,
            reservationNbGuests: guests,
            reservationStartTime: moment(reservation.start_time).format("dddd LL " + constants.AT_TEXT + " LT"),
            start_time_date: moment(reservation.start_time).format("DD/MM/YYYY"),
            start_time_time: moment(reservation.start_time).format("HH:mm")
        }
    )
};

export const isReservationOverlaping = (reservationA, reservationB) => {

    const tables = [...reservationA.tables, ...reservationB.tables];
    const tableSet = new Set(tables);

    if(tableSet.size === tables.length) {
        return false;
    }

    const start_time = moment(reservationB.start_time);
    const duration = moment.duration(reservationB.duration);
    const end_time = moment(start_time).add(duration);

    const current_start_time = moment(reservationA.start_time);
    const current_duration = moment.duration(reservationA.duration);
    const current_end_time = moment(current_start_time).add(current_duration);

    return current_start_time.isBetween(start_time, end_time, undefined, "[)") || start_time.isBetween(current_start_time, current_end_time, undefined, "[)");
}

export const isReservationOverlapingOnList = (currentReservation, reservations = []) => {
    for (const reservation of reservations) {
        if (reservation.id == currentReservation.id)
            continue; 
        const isOverlaping = isReservationOverlaping(currentReservation, reservation);
        if(isOverlaping) return true;
    }
    return false;
};

export const getReservations = (reservations) => reservations?.map(reservation => getReservation(reservation)) ?? [];

export const getTableName = (reservation, tables) => {
    if (reservation.tables){
        const table_names = tables.length > 0 ?
            reservation.tables.map(tableId => tables.find(table => table.id === tableId)?.name) :
            [];
        const table_text = reservation.tables.length > 1 ? constants.TABLES_TEXT : constants.TABLE_TEXT;
        return {
            ...reservation,
            reservationTables: reservation.tables.length > 0 ? table_text + ": " + table_names.join(',') : "",
            reservationTablesNumber: table_names
        }
    }
};

export const getTableNames = (reservations, tables) => reservations.map(reservation => getTableName(reservation, tables));

export const getEventName = event => constants.eventTypes.find(eventType => eventType.value === event)?.text;

/**
 * 
 * @param {number[]} ids 
 * @param {any[]} options 
 * @param {any[]} categories 
 * @returns {any[]} 
 */
export const getSelectedSpecialOptions = (ids, options, categories) => {

    if(ids.length === 0)
        return [];

    const filteredOptions = options
        .filter(option => ids.includes(option.id));
    return categories
        .map(category => ({
            ...category,
            options: filteredOptions.filter(option => option.category === category.id),
        }))
        .filter(category => category.options.length > 0)
        .sort(sortByIdCallback);
};

export const getDuration = duration => {
    const [hours, minutes, seconds] = duration.split(":");
    return hours + constants.HOUR_SHORT_TEXT + minutes
};

export const setTimeFromString = (momentObj = moment(), time = "00:00:00") => {
    const [hours, minutes, seconds] = time.split(':').map(el => parseInt(el, 10));
    return moment(momentObj).hours(hours).minutes(minutes).seconds(seconds);
}

export const getNbReservations = reservations => reservations.length;

export function getTablesSeatsSum(tables) {
    return tables?.reduce((totalSeats, table) => totalSeats + table.seats, 0) ?? 0;
}

export function getReservationsGuestsSum(reservations) {
    return reservations?.reduce((totalGuests, reservation) =>
        totalGuests + reservation.adults + reservation.children
        , 0) ?? 0;
}

export const getNbGuests = getReservationsGuestsSum;

export const getConfirmedReservations = reservations => {
    return reservations.filter(reservation => reservation.confirmed === true && reservation.cancelled === false);
};

export const getPendingReservations = reservations => {
    return reservations.filter(reservation => reservation.confirmed === false && reservation.cancelled === false);
};

export const getCancelledReservations = reservations => {
    return reservations.filter(reservation => reservation.cancelled === true);
};

export const getNumberConfirmedReservations = reservations => {
    return getConfirmedReservations(reservations).length;
};

export const getNumberPendingReservations = reservations => {
    return getPendingReservations(reservations).length;
};

export const getNumberCancelledReservations = reservations => {
    return getCancelledReservations(reservations).length;
};

export const getSelectedDayReservations = (reservations, selectedDay) => {
    return reservations.filter(reservation => moment(reservation.start_time).isSame(selectedDay, 'day'));
};

export const groupReservationsByShifts = (reservations = [], shifts = [], selectedDay = moment(), status=null, showOld=true) => {

    const effectiveShifts = getActualShifts(selectedDay, shifts);

    return (showOld ? reservations: filterOldReservations(reservations))
        .filter(reservation => (
            (status === constants.PENDING_MENU && (!reservation.cancelled && !reservation.confirmed)) ||
            (status === constants.CONFIRMED_MENU && (!reservation.cancelled && reservation.confirmed)) ||
            (status === constants.CANCELLED_MENU && (reservation.cancelled)) ||
            (!status)
        ))
        .reduce((groups, reservation) => {
            const startTime = moment(reservation.start_time);

            if(!moment(startTime).startOf('day').isSame(moment(selectedDay).startOf('day'))) {
                return groups;
            }

            const foundShift = effectiveShifts.find(shift => {
                const from = setTimeFromString(selectedDay, shift.from_hour);
                const to = setTimeFromString(selectedDay, shift.to_hour);
                return startTime.isBetween(from, to, undefined, "[)");
            }) ?? null;

            let key = startTime.format(constants.DISPLAY_TIME_FORMAT);

            if(foundShift) {
                const from = setTimeFromString(selectedDay, foundShift.from_hour).format(constants.DISPLAY_TIME_FORMAT);
                const to = setTimeFromString(selectedDay, foundShift.to_hour).format(constants.DISPLAY_TIME_FORMAT);
                key = `${from} - ${to}`;
            }

            if(!groups.has(key)) {
                groups.set(key, { shift: foundShift, reservations: [],});
            }

            const group = groups.get(key);
            groups.set(key, {
                ...group,
                reservations: [
                    ...group.reservations,
                    reservation,
                ],
            });

            return groups;
        }, new Map());
}

export const splitReservationsByStartTime = (reservations, shifts) => {
    if (reservations.length === 0) return [];

    const reservationMoment = moment(reservations[0].start_time);

    const reservationsDate = reservationMoment.format("YYYY-MM-DD");
    const reservationsDay = reservationMoment.isoWeekday() - 1;
    const dayShifts = getActualShifts(reservationMoment, shifts);
    const augmentedDayShifts = dayShifts.map(dayShift => {
        return {
            ...dayShift,
            shift_start_time: moment(reservationsDate + " " + dayShift.from_hour, "YYYY-MM-DD HH:mm:ss"),
            shift_end_time: moment(reservationsDate + " " + dayShift.to_hour, "YYYY-MM-DD HH:mm:ss")
        }
    })

    let current_group = null;
    let current_array = [];
    let group_array = [];

    reservations.forEach( reservation => {
        let el_group = null;
        const reservationStartTime = moment(reservation.start_time);

        augmentedDayShifts.forEach(augmentedShift => {
            if (reservationStartTime >= augmentedShift.shift_start_time && reservationStartTime < augmentedShift.shift_end_time) {
                el_group = augmentedShift.shift_start_time.format("HH:mm") + " - " + augmentedShift.shift_end_time.format("HH:mm")
            }
        })

        if (!el_group) {
            el_group = reservationStartTime.format("HH:mm");
        }

        const augmentedReservation = {
            ...reservation,
            reservationGroup: el_group
        }

        if (current_group === null) {
            current_array.push(augmentedReservation);
            current_group = el_group;
        }
        else {
            if (el_group === current_group) {
                current_array.push(augmentedReservation)
            }
            else {
                group_array.push(current_array);
                current_array = [augmentedReservation];
                current_group = el_group;
            }
        }
    });
    group_array.push(current_array);
    return group_array;
};

// export const splitReservationsByStartTime = reservations => {
//     if (reservations.length === 0) return [];
//
//     let current_group = null;
//     let current_array = [];
//     let group_array = [];
//
//     reservations.forEach( reservation => {
//         const el_group = moment(reservation.start_time).format("HH:mm");
//         if (current_group === null) {
//             current_array.push(reservation);
//             current_group = el_group;
//         }
//         else {
//             if (el_group === current_group) {
//                 current_array.push(reservation)
//             }
//             else {
//                 group_array.push(current_array);
//                 current_array = [reservation];
//                 current_group = el_group;
//             }
//         }
//     });
//     group_array.push(current_array);
//
//     return group_array;
// };

export const getCalendarSummary = (reservations = []) => {
    return reservations
        .filter(reservation => !reservation.cancelled)
        .reduce((dict, reservation) => {
            const startTime = moment(reservation.start_time);
            const dateKey = startTime.format(constants.DATE_FORMAT);
            const dateValue = dict[dateKey] ?? { total_seats: 0, reservations_count: 0 }; 
            const partySize = reservation.adults + reservation.children;
            dict[dateKey] = { total_seats: dateValue.total_seats + partySize, reservations_count: dateValue.reservations_count + 1 };
            return dict;
        }, {});
};

export const getTimelineItems = reservations => {
    const processedReservation = [];
    const colors = constants.TIMELINE_ITEMS_COLORS;

    let colorIdx = 0;

    const getClassName = (reservation) => {
        if (reservation.should_be_called && !reservation.has_been_called) {
            return "should_be_called";
        }
        else if (reservation.confirmed) {
            if (reservation.arrived) {
                return "arrived";
            }
            else if (reservation.no_show) {
                return "no_show";
            }
            else {
                return "confirmed";
            }
        }
        else {
            return "pending";
        }
    }

    reservations.map(
        reservation => {
            const durationMoment = moment.duration(reservation.duration);
            const itemStartTime = moment(reservation.start_time);
            const itemEndTime = moment(reservation.start_time).add(durationMoment);
            const className = getClassName(reservation);

            if (!reservation.cancelled) {
                if (reservation.tables.length > 1) {
                    reservation.tables.map(
                        table => {
                            processedReservation.push({
                                ...reservation,
                                timelineGroupId: table,
                                timelineGroupName: table,
                                timelineItemId: reservation.id + '-' + String(table),
                                timelineItemStartTime: itemStartTime,
                                timelineItemEndTime: itemEndTime,
                                className: className,
                                timelineColor: colors[colorIdx]
                            });
                        }
                    );
                    colorIdx += 1;
                    colorIdx = colorIdx % colors.length;
                } else {
                    processedReservation.push({
                        ...reservation,
                        timelineGroupId: reservation.tables[0],
                        timelineGroupName: reservation.tables[0],
                        timelineItemId: reservation.id.toString(),
                        timelineItemStartTime: itemStartTime,
                        timelineItemEndTime: itemEndTime,
                        className: className
                    });
                }
            }
        }
    );
    return processedReservation;
};

export const getEmptyCurrentReservation = () => {
    return {
        id: null,
        customer_id: null,
        first_name: "",
        last_name: "",
        start_time: moment().startOf("hour").add(1, "hour"),
        duration: null,
        adults: 0,
        children: 0,
        event: constants.eventTypes[0].value,
        custom_event: null,
        cake: false,
        stroller: false,
        highchair: false,
        reduced_mobility: false,
        terrace: false,
        comment: "",
        tables: [],
        confirmed: true,
        confirmed_at: null,
        created_at: null,
        updated_at: null,
        customer: null,
        token: null,
        cancelled: false,
        cancelled_by: null,
        cancelled_at: null,
        no_show: false,
        no_show_at: null,
        read: false,
        hidden: false,
        restaurant_cancel_message: ""
    };
};

export const getReservationStatus = reservation => {
    const { cancelled, confirmed, hidden, } = reservation;

    if (hidden)
        return constants.RESERVATION_STATUS.ARCHIVED;

    else if (cancelled)
        return constants.RESERVATION_STATUS.CANCELLED;

    else if (confirmed)
        return constants.RESERVATION_STATUS.CONFIRMED;

    return constants.RESERVATION_STATUS.PENDING;
};

export const emptyTablesForPending = (reservation) => {
    return {
        ...reservation,
        tables: reservation.confirmed? reservation.tables: [],
    };
}


// WEBPACK FOOTER //
// ./src/helpers/reservations.js